import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    grid-template-columns: 30fr 70fr;
    gap: 1rem;
  }

  > :last-child {
    padding-top: 1rem;
  }
`;

const AboutPane = styled.aside`
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    padding-right: 3rem;
  }

  ${(props) =>
    props.$compact &&
    `
    @media (max-width: 1023px) {
      display: none;
    }
  `}
`;

const CompactHeader = styled.div`
  display: none;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  @media (max-width: 1023px) {
    display: flex;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const CompactProfileImage = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border-color);
  display: block;
`;

const CompactName = styled.span`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);

  a {
    color: var(--text-primary);
    border-bottom: 1px solid transparent;

    &:hover {
      border-bottom-color: currentColor;
    }
  }
`;

const ProfileLink = styled(Link)`
  display: block;
  width: fit-content;
  border-bottom: none;

  &:hover,
  &:focus {
    border-bottom: none;
  }
`;

const ProfileImage = styled.img`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border-color);
  margin-bottom: 1rem;
  display: block;
`;

const IntroTitle = styled.h2`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 1.6875rem;
  font-weight: 400;
  letter-spacing: -0.02rem;
  line-height: 1.25;
  margin-top: 0;
  margin-bottom: 0.5rem;

  a {
    color: var(--text-primary);
    border-bottom: 1px solid transparent;

    &:hover {
      border-bottom-color: currentColor;
    }
  }
`;

const Subtitle = styled.h3`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 0;
  margin-bottom: 1.25rem;
`;

const IntroBody = styled.div`
  > p {
    font-size: 1.125rem;
    line-height: 1.6;
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--text-primary);
  }
`;

const IntroductionDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--divider-color);
  margin: 1.5rem 0;
`;

const Credentials = styled.div`
  > p {
    font-size: 1rem;
    line-height: 1.6;
    margin: 0 0 0.75rem;
    color: var(--text-primary);
  }
`;

const CredentialsHeading = styled.h3`
  margin: 0 0 0.75rem;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: var(--text-primary);
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0;
  margin-bottom: 0.875rem;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  color: var(--text-muted);

  a {
    color: var(--accent);
    border-bottom: 1px solid transparent;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      color: var(--accent-hover);
      border-bottom-color: currentColor;
    }
  }

  span.dot {
    opacity: 0.5;
  }
`;

function PortfolioSidebar({ compact }) {
  const scrollToTop = () => window.scrollTo(0, 0);

  return (
    <>
      {compact && (
        <CompactHeader>
          <ProfileLink to="/" aria-label="Go to all projects" onClick={scrollToTop}>
            <CompactProfileImage src="/profile_sjur.jpeg" alt="Sjur Sundin" />
          </ProfileLink>
          <CompactName>
            <Link to="/" onClick={scrollToTop}>Sjur Sundin</Link>
          </CompactName>
        </CompactHeader>
      )}
      <AboutPane $compact={compact}>
        <ProfileLink to="/" aria-label="Go to all projects" onClick={scrollToTop}>
          <ProfileImage src="/profile_sjur.jpeg" alt="Sjur Sundin" />
        </ProfileLink>
        <IntroTitle>
          <Link to="/" onClick={scrollToTop}>Sjur Sundin</Link>
        </IntroTitle>
        <Subtitle>AI Product Lead & Design Director</Subtitle>
        <SocialLinks>
          <Link to="/" onClick={scrollToTop}>sjurs.com</Link>
          <span className="dot">•</span>
          <a href="https://www.linkedin.com/in/sjurs/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <span className="dot">•</span>
          <a href="https://github.com/sjur" target="_blank" rel="noopener noreferrer">GitHub</a>
        </SocialLinks>
        <IntroBody>
          <p>
            I lead the development of governed AI products at Sopra Steria, turning expert judgment into systems for consultant CV quality, staffing, and client bids. My work spans product strategy, agent design, evaluation, user experience, governance, and delivery. In 2026 our CV evaluation product has more than doubled the share of high-quality CVs and eliminated most manual review.
          </p>
          <p>
            Independently, I am designing and building SitesAI, an agentic retail analyst that helps brands identify promising markets for expansion. The product grew from a hypothesis I developed while researching retail expansion as a Product Manager at Unacast.
          </p>
          <p>
            My foundation is fifteen years across six startups and large enterprises in California and Norway. I spent most of that career as a hands-on Head of Design or Design Director before moving into product management and AI product leadership. The common thread has been learning complex domains, understanding how experts make decisions, and turning that knowledge into products other people can use.
          </p>
        </IntroBody>
        <IntroductionDivider />
        <CredentialsHeading>Education and certifications</CredentialsHeading>
        <Credentials>
          <p><b>Certified Claude Architect - Foundations (CCA-F)</b>, Anthropic, 2026</p>
          <p><b>B.Sc. Computer Science</b><br/>University of Oslo, 2008</p>
          <p><b>MFA coursework Interactive Design and New Media</b>, Academy of Art University<br/>San Francisco, California, 2010 - 2013</p>
        </Credentials>
      </AboutPane>
    </>
  );
}

export default function PortfolioLayout({ children, compact }) {
  return (
    <MainLayout>
      <PortfolioSidebar compact={compact} />
      {children}
    </MainLayout>
  );
}
